import { Connection } from '@solana/web3.js';
import { JITO_RPC_URL, JITO_RPC_URL_2, JITO_RPC_URL_3 } from '../../../utils/config/environment';
export class SolanaLoadBalancedClientManager {
    constructor() {
        this.urls = [JITO_RPC_URL, JITO_RPC_URL_2, JITO_RPC_URL_3];
        this.windowSize = 1000;
        this.metricsWindowSize = 60000;
        this.endpoints = this.urls.map((url) => ({
            url,
            rateLimit: 15,
            lastCallTime: 0,
            callCount: 0,
            windowStart: Date.now(),
            requestTimes: [], // Initialize empty array for tracking request times
        }));
        this.metrics = {
            totalRequests: 0,
            totalWaits: 0,
            totalWaitTime: 0,
            requestsPerEndpoint: new Map(this.urls.map((url) => [url, 0])),
            averageWaitTime: 0,
            peakRequestsPerSecond: 0,
            lastRequestTimes: [],
            errors: new Map(),
        };
        // this.startMetricsLogging();
    }
    static getInstance() {
        if (!SolanaLoadBalancedClientManager.instance) {
            SolanaLoadBalancedClientManager.instance = new SolanaLoadBalancedClientManager();
        }
        return SolanaLoadBalancedClientManager.instance;
    }
    startMetricsLogging() {
        setInterval(() => {
            this.logMetrics();
        }, 5000); // Log every 5 seconds
    }
    updateRequestsPerSecond() {
        const now = Date.now();
        // Remove requests older than our metrics window
        this.metrics.lastRequestTimes = this.metrics.lastRequestTimes.filter((time) => now - time < this.metricsWindowSize);
        // Calculate current requests per second
        const requestsPerSecond = this.metrics.lastRequestTimes.length / (this.metricsWindowSize / 1000);
        this.metrics.peakRequestsPerSecond = Math.max(this.metrics.peakRequestsPerSecond, requestsPerSecond);
    }
    logMetrics() {
        const now = Date.now();
        console.log('\n=== Load Balancer Metrics ===');
        console.log(`Timestamp: ${new Date(now).toISOString()}`);
        console.log(`Total Requests: ${this.metrics.totalRequests}`);
        console.log(`Current Requests/Second: ${this.metrics.lastRequestTimes.filter((time) => now - time < 1000).length}`);
        console.log(`Peak Requests/Second: ${this.metrics.peakRequestsPerSecond.toFixed(2)}`);
        console.log(`Total Wait Events: ${this.metrics.totalWaits}`);
        console.log(`Average Wait Time: ${this.metrics.averageWaitTime.toFixed(2)}ms`);
        console.log('\nEndpoint Status:');
        this.endpoints.forEach((endpoint) => {
            const remainingCapacity = endpoint.rateLimit - endpoint.callCount;
            const timeSinceLastReset = now - endpoint.windowStart;
            console.log(`\nURL: ${endpoint.url}`);
            console.log(`- Current Call Count: ${endpoint.callCount}`);
            console.log(`- Remaining Capacity: ${remainingCapacity}`);
            console.log(`- Time Until Reset: ${Math.max(0, this.windowSize - timeSinceLastReset)}ms`);
            console.log(`- Total Requests: ${this.metrics.requestsPerEndpoint.get(endpoint.url)}`);
        });
        if (this.metrics.errors.size > 0) {
            console.log('\nErrors:');
            this.metrics.errors.forEach((count, error) => {
                console.log(`${error}: ${count} occurrences`);
            });
        }
    }
    calculateOptimalWaitTime(endpoint) {
        const now = Date.now();
        // If we're in a new window and haven't reset yet, reset first
        if (now - endpoint.windowStart >= this.windowSize) {
            this.resetWindowIfNeeded(endpoint);
            return 0; // No need to wait if we just reset
        }
        // If we have capacity in the current window, no need to wait
        if (endpoint.callCount < endpoint.rateLimit) {
            return 0;
        }
        // Calculate time until the current window ends
        const timeUntilWindowReset = this.windowSize - (now - endpoint.windowStart);
        // If we're close to a window reset, wait for the reset
        if (timeUntilWindowReset < 100) {
            // If less than 100ms until reset
            return timeUntilWindowReset + 10; // Add 10ms buffer
        }
        // If we have request timestamps, calculate when the next slot will open
        if (endpoint.requestTimes.length >= endpoint.rateLimit) {
            // Get the oldest request in the current window
            const oldestRequest = endpoint.requestTimes[endpoint.requestTimes.length - endpoint.rateLimit];
            const timeUntilSlotFrees = oldestRequest + this.windowSize - now;
            // Return the time until the next slot frees up (with a small buffer)
            return Math.max(timeUntilSlotFrees + 10, 0);
        }
        // Fallback: wait for window reset
        return timeUntilWindowReset;
    }
    resetWindowIfNeeded(endpoint) {
        const now = Date.now();
        if (now - endpoint.windowStart >= this.windowSize) {
            endpoint.windowStart = now;
            endpoint.callCount = 0;
            // Clear old request times
            endpoint.requestTimes = endpoint.requestTimes.filter((time) => now - time < this.windowSize);
        }
    }
    async sleep(ms) {
        const startWait = Date.now();
        await new Promise((resolve) => setTimeout(resolve, ms));
        this.metrics.totalWaits++;
        const waitTime = Date.now() - startWait;
        this.metrics.totalWaitTime += waitTime;
        this.metrics.averageWaitTime = this.metrics.totalWaitTime / this.metrics.totalWaits;
    }
    getBestEndpoint() {
        const now = Date.now();
        this.endpoints.forEach((endpoint) => this.resetWindowIfNeeded(endpoint));
        let bestEndpoint = null;
        let shortestWaitTime = Infinity;
        for (const endpoint of this.endpoints) {
            const waitTime = this.calculateOptimalWaitTime(endpoint);
            // // Log endpoint status for debugging
            // console.log(
            //   `Endpoint ${endpoint.url}: Wait time ${waitTime}ms, Calls: ${endpoint.callCount}/${endpoint.rateLimit}`
            // );
            if (waitTime < shortestWaitTime) {
                shortestWaitTime = waitTime;
                bestEndpoint = endpoint;
            }
        }
        return { endpoint: bestEndpoint, waitTime: shortestWaitTime };
    }
    async getConnection() {
        const startTime = Date.now();
        let attempts = 0;
        try {
            while (true) {
                attempts++;
                const { endpoint, waitTime } = this.getBestEndpoint();
                if (!endpoint) {
                    throw new Error('No endpoints available');
                }
                if (waitTime > 0) {
                    // console.log(`Waiting ${waitTime}ms for next available slot on ${endpoint.url}`);
                    await this.sleep(waitTime);
                    continue;
                }
                // Update endpoint metrics
                endpoint.lastCallTime = Date.now();
                endpoint.callCount++;
                endpoint.requestTimes.push(Date.now());
                // Cleanup old request times
                endpoint.requestTimes = endpoint.requestTimes.filter((time) => Date.now() - time < this.windowSize);
                // Update global metrics
                this.metrics.totalRequests++;
                this.metrics.requestsPerEndpoint.set(endpoint.url, (this.metrics.requestsPerEndpoint.get(endpoint.url) || 0) + 1);
                this.metrics.lastRequestTimes.push(Date.now());
                this.updateRequestsPerSecond();
                if (attempts > 1) {
                    console.log(`Required ${attempts} attempts and ${Date.now() - startTime}ms to get connection`);
                }
                return new Connection(endpoint.url);
            }
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.metrics.errors.set(errorMessage, (this.metrics.errors.get(errorMessage) || 0) + 1);
            throw error;
        }
    }
    getMetrics() {
        return Object.assign({}, this.metrics);
    }
    getEndpointStatuses() {
        const now = Date.now();
        return this.endpoints.map((endpoint) => ({
            url: endpoint.url,
            callCount: endpoint.callCount,
            rateLimit: endpoint.rateLimit,
            remainingCapacity: endpoint.rateLimit - endpoint.callCount,
            timeUntilReset: Math.max(0, this.windowSize - (now - endpoint.windowStart)),
        }));
    }
}
